"use client"

import { useState } from "react"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"

const WEB3FORMS_ACCESS_KEY = "be7f04c0-0b69-4abc-9c8b-79b571407069"

export function ContactForm() {
  const [result, setResult] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSubmitting(true)
    setResult(null)

    const form = event.currentTarget
    const formData = new FormData(form)
    formData.append("access_key", WEB3FORMS_ACCESS_KEY)

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData,
      })

      const data = await response.json()
      setResult(data.success ? "Message sent successfully! We'll get back to you soon." : data.message || "Something went wrong. Please try again.")
    } catch {
      setResult("Failed to send. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="max-w-2xl space-y-8"
    >
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-2">
          <Label
            htmlFor="contact-name"
            className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground"
          >
            Name
          </Label>
          <Input
            id="contact-name"
            name="name"
            type="text"
            placeholder="Your name"
            required
            className="font-sans border-border/50 bg-input/30 h-12 rounded-none focus-visible:border-accent focus-visible:ring-accent/30"
          />
        </div>
        <div className="space-y-2">
          <Label
            htmlFor="contact-email"
            className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground"
          >
            Email
          </Label>
          <Input
            id="contact-email"
            name="email"
            type="email"
            placeholder="you@example.com"
            required
            className="font-sans border-border/50 bg-input/30 h-12 rounded-none focus-visible:border-accent focus-visible:ring-accent/30"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label
          htmlFor="contact-subject"
          className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground"
        >
          Subject
        </Label>
        <Input
          id="contact-subject"
          name="subject"
          type="text"
          placeholder="Project inquiry, partnership, etc."
          className="font-sans border-border/50 bg-input/30 h-12 rounded-none focus-visible:border-accent focus-visible:ring-accent/30"
        />
      </div>

      <div className="space-y-2">
        <Label
          htmlFor="contact-message"
          className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground"
        >
          Message
        </Label>
        <Textarea
          id="contact-message"
          name="message"
          placeholder="Tell us about your project or how we can help..."
          required
          rows={5}
          className="font-sans border-border/50 bg-input/30 min-h-[140px] rounded-none focus-visible:border-accent focus-visible:ring-accent/30 resize-none"
        />
      </div>

      <div className="space-y-4">
        <Button
          type="submit"
          disabled={isSubmitting}
          className="group inline-flex items-center gap-3 border border-foreground/20 px-8 py-3 h-auto font-mono text-sm uppercase tracking-widest bg-transparent text-foreground hover:bg-transparent hover:border-accent hover:text-accent transition-all duration-200 rounded-none disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? "Sending..." : "Send Message"}
          {!isSubmitting && (
            <svg
              className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          )}
        </Button>
        {result && (
          <p className={`font-sans text-sm ${result.includes("successfully") ? "text-green-600 dark:text-green-400" : "text-destructive"}`}>
            {result}
          </p>
        )}
      </div>
    </form>
  )
}
